import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { Checkbox } from '@calendar/components/ui/checkbox';
import { Label } from '@calendar/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@calendar/components/ui/select';
import { cn, getFullMonthFromShort, getSeason, rotateMonths } from '@calendar/lib/utils';
import Container from './custom/container';
import { SEASONS } from '@calendar/lib/constants';
import type { View, Category } from '@/types/calendar';
import { ListFilterIcon, EyeIcon } from 'lucide-react';

// Extend Window interface to include our preset filters
declare global {
  interface Window {
    curtisCalendarPresetFilters?: Partial<FilterState>;
  }
}

// Types
export interface FilterState {
  season: string
  month: string | null
  category: string | undefined
  freeOnly: boolean
  ticketedOnly: boolean
  upcomingOnly: boolean
  sortBy: string
  view: string
}

export interface CalendarFiltersProps {
  setFilters: (filters: FilterState) => void
  filters: FilterState
  categories: Category[]
  views: View[]
  className?: string
  count: number
}

export default function CalendarFilters({
  setFilters,
  filters,
  categories,
  views,
  className,
  count,
}: CalendarFiltersProps) {
  // Detect if we have preset filters that should be protected
  const hasPresetCategory = window.curtisCalendarPresetFilters?.category !== undefined;
  
  // Update filters and notify parent
  const updateFilters = (newFilters: Partial<FilterState>) => {
    const updatedFilters = { ...filters, ...newFilters };

    if (updatedFilters.upcomingOnly) {
      updatedFilters.month = null;
    }

    // Preserve preset filters from being overridden
    if (hasPresetCategory && window.curtisCalendarPresetFilters?.category) {
      updatedFilters.category = window.curtisCalendarPresetFilters.category;
    }

    setFilters(updatedFilters);
  };

  const handleUpcomingChange = (upcoming: boolean) => {
    if(!upcoming) {
      updateFilters({ upcomingOnly: false });
      return;
    }

    /* Get the current, active season from today's date */
    const today = new Date();
    const currentSeason = getSeason(today);
    updateFilters({ upcomingOnly: true, season: currentSeason, view: filters.view });
  };

  const handleMonthChange = (month: string | null) => {
    // If month is null, do nothing
    if (month === null) {
      return;
    }

    // Set newMonth based on whether it matches the current month
    const newMonth = filters.month && month === filters.month ? null : month;
    const newFilters = { ...filters, month: newMonth, upcomingOnly: false };

    updateFilters(newFilters);
  };

  const handleSeasonChange = (direction: 'previous' | 'next') => {
    const currentIndex = SEASONS.findIndex((s) => s.id === filters.season);
    
    // Add boundary checks to prevent undefined access
    if (direction === 'previous') {
      const nextIndex = currentIndex + 1;
      if (nextIndex >= SEASONS.length) {
        return; // Already at the oldest season, can't go further
      }
      const newSeason = SEASONS[nextIndex];
      updateFilters({ season: newSeason.id, upcomingOnly: false });
    } else if (direction === 'next') {
      const nextIndex = currentIndex - 1;
      if (nextIndex < 0) {
        return; // Already at the newest season, can't go further
      }
      const newSeason = SEASONS[nextIndex];
      updateFilters({ season: newSeason.id, upcomingOnly: false });
    }
  };

  const handleCategoryChange = (category: string | undefined) => {
    if(category === 'all') {
      updateFilters({ category: undefined });
      return;
    }

    // Search for category in both parent categories and their children
    let selectedCategory = categories.find((c) => c.id === category);

    // If not found in parent categories, search in children
    if(!selectedCategory) {
      for(const parentCategory of categories) {
        if(parentCategory.children && parentCategory.children.length > 0) {
          const childCategory = parentCategory.children.find((child) => child.id === category);
          if(childCategory) {
            selectedCategory = childCategory;
            break;
          }
        }
      }
    }

    if(!selectedCategory || selectedCategory.id === 'all') {
      updateFilters({ category: undefined });
      return;
    }

    updateFilters({ category: selectedCategory.id });
  };

  const handleViewChange = (view: string) => {
    updateFilters({ view });
  };

  // Get active month and season
  const rotatedMonths = rotateMonths();
  // Derive dynamic year labels based on seasonStart
  const [ startYearStr, endYearSuffix ] = filters.season.split('-');
  const firstYear = startYearStr;
  const secondYear = `20${endYearSuffix}`;
  const seasonStartStr = window?.curtisCalendar?.seasonStart;
  if (!seasonStartStr) throw new Error('window.curtisCalendar.seasonStart is not defined');
  const [ , seasonStartMonthNum ] = seasonStartStr.split('-').map(Number);
  const seasonStartMonthIndex = seasonStartMonthNum - 1;
  const monthsInFirstYear = 12 - seasonStartMonthIndex;
  const activeMonth = rotatedMonths.find((m) => filters?.month === m.id)?.name || 'All Months';
  const activeMonthIndex = rotatedMonths.findIndex((m) => filters?.month === m.id);
  const activeSeason = filters?.season || 'All Seasons';

  /* Get the active year for the month, rather than the season */
  const activeYear = activeMonthIndex < monthsInFirstYear ? firstYear : secondYear;

  /* Create the active date from the active month and season, but expand the month to the full month name */
  const fullMonth = getFullMonthFromShort(activeMonth);
  const activeDate = `${fullMonth ? fullMonth + ' ' : ''}${activeYear}`;

  let calendarTitle = 'All Events';
  if(filters.upcomingOnly) {
    calendarTitle = 'Upcoming Events';
  } else {
    if (filters.month) {
      calendarTitle = activeDate;
    }
    if (!filters.month && filters.season) {
      calendarTitle = `${activeSeason} Events`;
    }
  }

  return (
    <div className="app--calendar__filters" data-view={filters.view} data-count={count}>
      <Container className={cn('tw:bg-white', className)}>
        <div className="header-content">
          <div className="header-row">
            <div className="title-container">
              <div className="title">
                {calendarTitle}
              </div>
            </div>

            <div className="controls-container">
              <div className="checkbox-group">
                <div className="checkbox-container">
                  <Checkbox
                    id="upcoming-events"
                    checked={filters.upcomingOnly}
                    onCheckedChange={(checked) => handleUpcomingChange(checked === true)}
                  />
                  <Label htmlFor="upcoming-events">
                    Upcoming
                  </Label>
                </div>

                <div className="checkbox-container">
                  <Checkbox
                    id="free-events"
                    checked={filters.freeOnly}
                    onCheckedChange={(checked) => updateFilters({ freeOnly: checked === true })}
                  />
                  <Label htmlFor="free-events">
                    Free
                  </Label>
                </div>
              </div>

              <div className="select-group">
                {!hasPresetCategory && (
                  <Select key={filters.category} value={filters.category} onValueChange={(value) => handleCategoryChange(value)}>
                    <SelectTrigger icon={ListFilterIcon} className="filter-select">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent align="end" className="select-content">
                      <SelectItem value="all">All Events</SelectItem>

                      {categories.map((option) => {
                        if(option.children && option.children.length > 0) {
                          return (
                            <SelectGroup key={option.id} className="select-group">
                              <SelectLabel className="select-group-label">{option.name}</SelectLabel>
                              {option.children.map((child) => (
                                <SelectItem key={child.id} value={child.id} className="select-group-item">
                                  {child.name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          );
                        }

                        return (
                          <SelectItem key={option.id} value={option.id}>
                            {option.name}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}

                <Select key={filters.view} value={filters.view} onValueChange={(value) => handleViewChange(value)}>
                  <SelectTrigger icon={EyeIcon} className="view-select">
                    <SelectValue placeholder="View" />
                  </SelectTrigger>
                  <SelectContent align="end" className="select-content">
                    {views.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="month-navigation">
            <button
              className="nav-button"
              data-style="default"
              onClick={() => handleSeasonChange('previous')}
              disabled={SEASONS[SEASONS.length - 1].id === filters.season}
            >
              <ChevronLeftIcon />
            </button>

            <div className="month-scroll">
              {rotatedMonths.map((month, monthIndex) => (
                <button
                key={month.id}
                onClick={() => handleMonthChange(month.id)}
                data-style="default"
                className={`month-button ${filters.month === month.id ? 'active' : ''}`}
              >
                <span className={`month-year ${filters.month === month.id ? 'active' : ''}`}>
                  {monthIndex < monthsInFirstYear ? firstYear : secondYear}
                </span>
                <span className={`month-name ${filters.month === month.id ? 'active' : ''}`}>
                  {month.name}
                </span>
              </button>
              ))}
            </div>

            <button
              className="nav-button"
              data-style="default"
              onClick={() => handleSeasonChange('next')}
              disabled={SEASONS[0].id === filters.season}
            >
              <ChevronRightIcon />
            </button>
          </div>
        </div>
      </Container>
    </div>
  );
}

